import UtilMarker from "../utils/UtilMarker";

export default class MarkerManager {
    constructor(map) {
        if (MarkerManager.instance) {
            return MarkerManager.instance;
        }

        this.map = map;
        this.markers = {};
        this.clusters = {};
        this.currentZoom = null;
        this.loadedBound = null;
        this.currentIconType = 'default';
        MarkerManager.instance = this;
    }

    static getInstance(map = null) {
        if (!MarkerManager.instance && !map) {
            throw new Error("MarkerManager: требуется map при первой инициализации");
        }

        if (!MarkerManager.instance) {
            MarkerManager.instance = new MarkerManager(map);
        }

        return MarkerManager.instance;
    }

    setCurrentZoom(zoom) {
        this.currentZoom = zoom;
    }

    getCurrentZoom() {
        return this.currentZoom;
    }

    getMarkers(kitId) {
        return this.markers[String(kitId)] ?? [];
    }

    getMarkersWithoutPrices() {
        return this.markers['-2'] ?? [];
    }

    setLoadedBound(bound) {
        this.loadedBound = bound;
    }

    setCurrentIconType(currentIconType) {
        this.currentIconType = currentIconType;
    }

    getCurrentIconType() {
        return this.currentIconType;
    }

    removeObjects(currentKitId) {
        this.removeClusters(currentKitId)
        this.clearMarkers(currentKitId)
    }

    addObjects(currentKitId, color, {markers, clusters}, isMarkersActive = true, selectedCatalogIds = []) {
        this.addClusters(currentKitId, clusters);

        let tmpMarkers = UtilMarker.adapterMarkers(markers, color, currentKitId, false, this.currentIconType);

        if(String(currentKitId) === '-2') {
            tmpMarkers = tmpMarkers.map((marker) => {
                return UtilMarker.prepareWithoutPriceMarker(marker, color, selectedCatalogIds)
            });
        }

        if(String(currentKitId) === '0') {
            tmpMarkers = tmpMarkers.map((el) => {
                if (String(el.extraData.kitId) === String(currentKitId)) {
                    el.fill_color = el.color_legend;
                }
                return el;
            })
        }

        this.markers[currentKitId] = [...tmpMarkers]

        this.map.redrawMarkers((marker) => {
            const {extraData = {}} = marker;
            const {kitId = '-1'} = extraData;
            return String(kitId) === String(currentKitId);
        }, this.markers[currentKitId], isMarkersActive);
    }

    clearMarkers(currentKitId = '-1') {
        this.markers[currentKitId] = [];

        this.map.redrawMarkers((marker) => {
            const {extraData = {}} = marker;
            const {kitId = '-1'} = extraData;

            return String(kitId) === String(currentKitId);
        }, [], true);
    }

    removeClusters(kitId, isRender = true) {
        const removeQuadKeys = Object.values(this.clusters).reduce((curRemoveQuadKeys, cluster) => {
            const {quadkey} = cluster;

            let bufferCluster = this.clusters[quadkey] ?? null;
            if (bufferCluster === null) {
                return curRemoveQuadKeys;
            }

            const usedKits = bufferCluster.extraData.usedKits.filter(currentKitId => String(currentKitId) !== String(kitId) && String(currentKitId) !== '-2');
            bufferCluster.extraData = {
                ...bufferCluster.extraData,
                usedKits
            };

            if (!bufferCluster.extraData.usedKits.length) {
                curRemoveQuadKeys.push(quadkey)
            } else {
                this.clusters = {
                    ...this.clusters,
                    [quadkey]: bufferCluster
                };
            }
            return curRemoveQuadKeys;
        }, []);

        removeQuadKeys.forEach((removeQuadKey) => {
            if (removeQuadKey in this.clusters) {
                delete this.clusters[removeQuadKey]
            }
        })

        const clustersArray = Object.values(this.clusters);

        if (isRender) {
            this.map.redrawClusters(clustersArray);
        }

        return clustersArray;
    }

    /**
     * @param {Number} kitId
     * @param {Array} clusters
     */
    addClusters(kitId, clusters) {
        this.removeClusters(kitId, false);

        clusters.forEach((cluster) => {
            const {quadkey} = cluster;

            let bufferCluster = this.clusters[quadkey] ?? null;
            if (bufferCluster === null) {
                bufferCluster = {...cluster};
                bufferCluster.extraData = {
                    usedKits: []
                };
            }

            if (!bufferCluster.extraData.usedKits.includes(String(kitId))) {
                bufferCluster.extraData.usedKits.push(String(kitId));
            }

            this.clusters = {
                ...this.clusters,
                [quadkey]: bufferCluster
            };
        })

        const updatedClusters = Object.values(this.clusters);

        this.map.redrawClusters(updatedClusters);

        return updatedClusters;
    }

    /** Условие повторной загрузки маркеров */
    isNeedUpdateMarkers(bound, zoom) {
        const {latMin, latMax, lngMin, lngMax} = bound;
        return this.loadedBound !== null
            && (
                zoom !== this.currentZoom
                || latMin < this.loadedBound.latMin
                || latMax > this.loadedBound.latMax
                || lngMin < this.loadedBound.lngMin
                || lngMax > this.loadedBound.lngMax
            )
    }

    /**
     * Логика показа/скрытия одного маркера
     */
    static updateMarkerVisibility(marker, activeStatuses = []) {
        const { extraData = {} } = marker;
        const { kitId: markerKitId = '-1', status = '', type = 'default', sideStatus = {} } = extraData;

        if (type === 'poi') return; // POI не трогаем

        if (String(markerKitId) === '0') {
            const sidesFilteredKeys = Object.keys(sideStatus)
                .filter(sideKey => activeStatuses.includes(String(sideStatus[sideKey])));

            marker.sidesFiltered = sidesFilteredKeys.map(sideKey => String(sideKey).toUpperCase());

            if (marker.sidesFiltered.length > 0) {
                marker.updateIcon(marker.iconType, false);
                marker.drawMarker();
            } else {
                marker.clearMarker();
            }
        }

        if (String(markerKitId) === '-2') {
            if (activeStatuses.includes(status)) {
                marker.drawMarker();
            } else {
                marker.clearMarker();
            }
        }

        if (String(markerKitId) === '-1') {
            marker.clearMarker();
        }
    }

    /**
     * Обновляем ВСЕ маркеры на карте
     */
    updateMarkers(activeStatuses = []) {
        this.map.updateMarkers(marker => {
            MarkerManager.updateMarkerVisibility(marker, activeStatuses);
        });
    }

    hideMarkers(kitIds) {
        this.map.updateMarkers((marker) => {
            const {extraData = {}} = marker;
            const {kitId: markerKitId = '-1', type = 'default'} = extraData;

            if (type === 'poi') return; // POI не трогаем

            if (kitIds.includes(String(markerKitId))) {
                marker.clearMarker();
            }
        });
    }

    setMarkerClicked(markerId, kitIdClicked) {
        let foundMarkers = this.map.$refs.linceMapWrapper.serviceMap.markers.filter((marker) => {
            const {id = '-2', extraData = {}} = marker;
            const {kitId = "0"} = extraData;
            return String(id) === markerId && kitId === kitIdClicked;
        });

        if(foundMarkers.length){
            this.map.$refs.linceMapWrapper.serviceMap.setMarkerClicked(foundMarkers[0]);
        }
    }

    drawMarkers(kitIds) {
        this.map.updateMarkers((marker) => {
            const {extraData = {}} = marker;
            const {kitId: markerKitId = '-1', type = 'default'} = extraData;
            if (type !== 'poi' && kitIds.includes(String(markerKitId))) {
                marker.drawMarker();
            }
        });
    }

    updateLegendMarkers({ attribute, legendKey, groupId, color, figure, active }) {
        this.map.updateMarkers((marker) => {
            const {
                extraData = {},
                isVisible = false,
                sidesFiltered: sidesFilteredMarker = [],
            } = marker;
            const { kitId = '', side_names: sideNamesMarker = [] } = extraData;

            // 1. Атрибут не "default"
            if (attribute !== 'default') {
                const legendKeyMarker = UtilMarker.getLegendKeyByKitId(kitId);
                if (legendKeyMarker !== String(legendKey)) {
                    return this._finalizeMarker(marker, isVisible);
                }

                const attributesCorrect = {
                    status: 'side_status',
                    side_name: 'side_names',
                };
                const attributeCorrect = attributesCorrect[attribute] ?? String(attribute);
                const attributeDataMarker = extraData[attributeCorrect] ?? {};

                // Приводим данные атрибутов к единому виду
                const normalizeData = {
                    status: (data) =>
                        Object.keys(data).reduce((res, side) => {
                            res[side] = [data[side]];
                            return res;
                        }, {}),
                    side_name: (data) =>
                        data.reduce((res, side) => {
                            res[side] = [side];
                            return res;
                        }, {}),
                };
                const attributeDataMarkerCorrect = normalizeData[attribute]
                    ? normalizeData[attribute](attributeDataMarker)
                    : { ...attributeDataMarker };

                let updated = false;

                Object.entries(attributeDataMarkerCorrect).forEach(([side, groupIdsMarker]) => {
                    if (groupIdsMarker.includes(String(groupId))) {
                        if (active) {
                            marker.sidesColors[side] = String(color);
                            marker.figure = String(figure);
                            if (!sidesFilteredMarker.includes(String(side))) {
                                marker.sidesFiltered.push(String(side));
                            }
                        } else {
                            const idx = marker.sidesFiltered.findIndex(
                                (sideMarker) => String(sideMarker) === String(side),
                            );
                            if (idx >= 0) marker.sidesFiltered.splice(idx, 1);
                        }
                        UtilMarker.updateMarkerVisibility(marker);
                        updated = true;
                    }
                });

                if (updated) return;
            }
            // 2. Атрибут "default"
            else {
                if (String(kitId) === groupId) {
                    if (active) {
                        const sides = Object.keys(marker?.sidesColors ?? {});
                        sides.forEach((side) => (marker.sidesColors[side] = String(color)));
                        marker.figure = String(figure);
                        marker.sidesFiltered = [...sideNamesMarker];
                    } else {
                        marker.sidesFiltered = [];
                    }

                    UtilMarker.updateMarkerVisibility(marker);
                    return;
                }
            }

            // 3. Если до сюда дошли — применяем финальную обработку
            this._finalizeMarker(marker, isVisible);
        });
    }

    updateMarkersState({targetKitId, kitsMarkersActive, mapFoundMarkersSelectedIds, targetMarkers = null, bufferMarkersSelected = null, }) {
        this.map.updateMarkers((marker) => {
            const {id = '-1', extraData = {}, state = -1} = marker;
            const {kitId = '-1'} = extraData;

            if (String(kitId) === String(targetKitId) && state === 1 && targetMarkers === null) {
                marker.updateState(0, kitsMarkersActive.includes(targetKitId));
            }

            else if(String(kitId) === String(targetKitId) && targetMarkers !== null && targetMarkers.includes(id) && !bufferMarkersSelected.includes(id)){
                marker.updateState(0, kitsMarkersActive.includes(targetKitId));
            }

            if (String(kitId) === String(targetKitId) && mapFoundMarkersSelectedIds.includes(String(id)) && targetMarkers === null) {
                marker.updateState(1, kitsMarkersActive.includes(targetKitId));
            }

            else if(String(kitId) === String(targetKitId) && targetMarkers !== null && targetMarkers.includes(id) && bufferMarkersSelected.includes(id)){
                marker.updateState(1, kitsMarkersActive.includes(targetKitId));
            }
        }, false);
    }

    updateWpMarkersState(mapWithoutPriceSelectedCatalogIds, isWpActive) {
        this.map.updateMarkers((marker) => {
            const {extraData = {}, state = -1} = marker;
            const {kitId = '-1', catalogSides = {}} = extraData;
            let isSelected = false;

            Object.values(catalogSides).forEach((catalogId) => {
                if(Object.keys(mapWithoutPriceSelectedCatalogIds).includes(catalogId)){
                    isSelected = true;
                }
            });

            if (String(kitId) === '-2' && !isSelected && state === 1) {
                marker.updateState(0, isWpActive);
            }

            if (String(kitId) === '-2' && isSelected) {
                marker.updateState(1, isWpActive);
            }
        }, false);
    }

    /**
     * Универсальная финализация маркера, если он не обновился по условиям
     */
    _finalizeMarker(marker, isVisible) {
        if (isVisible) {
            marker.updateIcon(marker.iconType, false);
            marker.drawMarker();
        } else {
            marker.clearMarker();
        }
    }
}
